import { createContext, createElement, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { clusters as clusterApi, nodes as nodeApi, review as reviewApi, teams as teamApi } from '@/api/endpoints'
import { usePoll } from '@/hooks/usePoll'
import { useSession } from '@/hooks/useSession'
import { useFallback, useRefetchOn } from '@/hooks/useEventStream'
import { EMPTY, build, type World } from '@/views/model'
import type { ApiNode, Cluster, ReviewItem } from '@/api/types'
import type { Change } from '@/realtime/stream'

// 관리자 껍데기가 늘 들고 있는 작은 목록 넷 — 클러스터·노드(수십 줄), 팀(팀 수만큼), 검토 대기(열린 결정만)를 각각 당긴다.
// VM·사람 수만큼 커지는 목록(임대·VM·계정)은 여기서 읽지 않는다 — 그것을 그리는 화면이 서버에 쪽으로 묻는다(`usePage`).
// 변경 알림은 그 종류의 목록만 다시 읽고, 알림이 없는 사이는 polling 이 메운다(스트림이 붙어 있으면 느긋하게).
const POLL_MS = 8000
const HEALTH_DEBOUNCE_MS = 10000
// 검토 대기는 노드·임대·클러스터·소유자의 이름 어느 쪽이 바뀌어도 낡는다. 한 번의 조작이 여러 프레임을 내므로 잠깐 모아 한 번 읽는다.
const REVIEW_DEBOUNCE_MS = 1000
// 노드의 할당과 VM 수도 VM·임대의 쓰기 하나가 여러 프레임을 내므로 같은 만큼 모은다.
const HELD_DEBOUNCE_MS = 1000
// 팀 줄의 VM 수도 VM·임대의 쓰기로 낡는다. 팀 목록은 멤버를 싣고 팀 수만큼 길어 노드보다 무거우므로 더 오래 모은다.
const TEAM_VMS_DEBOUNCE_MS = 5000

export type WorldRead = { world: World; loading: boolean; error: Error | null; refresh: () => void }

// 방금 쓴 것을 곧바로 다시 읽는다. 변경 스트림은 최고 관리자만 받으므로 관리자(제한)는 다음 폴링까지
// 방금 만든 팀이 목록에 없다 — 쓰기를 마친 화면이 이것을 부른다. Provider 는 App 의 Shell 이다.
// `World` 밖에서 목록을 읽는 화면(쪽으로 읽는 목록 `usePage` 와 그 밖의 읽기)도 여기에 자기 다시 읽기를 단다 —
// 달지 않으면 관리자가 쓴 직후 그 화면이 다음 폴링까지 옛 목록을 보인다.
type WorldRefresh = {
  refresh: () => void
  /** 쓰기 뒤에 함께 다시 읽을 것을 단다. 돌려준 함수가 뗀다. */
  register: (refresh: () => void) => () => void
}
export const WorldRefreshCtx = createContext<WorldRefresh>({ refresh: () => undefined, register: () => () => undefined })
export function useWorldRefresh(): () => void {
  return useContext(WorldRefreshCtx).refresh
}

/** 쓰기 뒤의 다시 읽기에 이 읽기를 단다. `refresh` 는 고정된 함수여야 한다(`usePoll` 의 것처럼). */
export function useRefreshAfterWrites(refresh: () => void): void {
  const { register } = useContext(WorldRefreshCtx)
  useEffect(() => register(refresh), [register, refresh])
}

/** `refresh`(보통 `useWorld` 의 것)와 단 읽기들을 함께 부르는 다시 읽기를 아래에 준다. */
export function WorldRefreshProvider({ refresh, children }: { refresh: () => void; children: ReactNode }) {
  // 단 읽기의 집합은 렌더와 무관한 하나의 객체이고 다는 함수도 하나다 — 단 쪽이 렌더마다 다시 달지 않는다.
  const [reads] = useState(() => new Set<() => void>())
  const register = useCallback(
    (read: () => void) => {
      reads.add(read)
      return () => {
        reads.delete(read)
      }
    },
    [reads],
  )
  const value = useMemo<WorldRefresh>(
    () => ({
      refresh: () => {
        refresh()
        for (const read of reads) read()
      },
      register,
    }),
    [refresh, register, reads],
  )
  return createElement(WorldRefreshCtx.Provider, { value }, children)
}

export function useWorld(): WorldRead {
  const interval = useFallback(POLL_MS)
  // 관리자(제한)에게는 플랫폼 자체가 없다(F-38). 읽을 수 없는 목록은 묻지도 않는다 —
  // 물으면 403 이 화면의 오류가 된다.
  const top = useSession().role === 'superadmin'
  const none = <T,>() => Promise.resolve([] as T[])
  const clusters = usePoll(top ? clusterApi.list : none<Cluster>, interval)
  const nodes = usePoll(top ? nodeApi.list : none<ApiNode>, interval)
  const teams = usePoll(teamApi.list, interval)
  const review = usePoll(top ? reviewApi.list : none<ReviewItem>, interval)

  // 노드의 상태·살아 있음·소속·진행이 바뀌면 노드와 클러스터 집계가 함께 낡는다.
  // 하트비트(seen)마다 목록을 다시 읽지는 않는다 — 살아 있음의 전이가 따로 온다.
  // 장비 점검과 A-5 검증은 **다시 듣는다**(A6-17): 진행이 그 둘을 포함하게 된
  // 뒤로는 그것이 곧 진행의 변화이고, 빼 두면 점검(최대 8분)과 검증(최대 20분)
  // 동안 화면이 멎는다.
  useRefetchOn(
    (c) => c.kind === 'node' && c.what !== 'seen',
    () => {
      nodes.refresh()
      clusters.refresh()
    },
  )
  useRefetchOn((c) => c.kind === 'cluster' && c.what !== 'health', clusters.refresh)
  useRefetchOn((c) => c.kind === 'team', teams.refresh)
  useRefetchOn((c) => c.kind === 'cluster' && c.what === 'health', clusters.refresh, HEALTH_DEBOUNCE_MS)
  // 노드는 그 위의 VM 이 쥔 할당과 VM 수(`allocated`·`vm_count`)를 싣는다 — VM 을 만들고 지우고 디스크를 붙이고
  // 임대를 보존으로 돌리는 쓰기는 `vm`·`lease` 만 내므로 그 알림에도 노드를 다시 읽는다. 빠뜨리면 클러스터 화면의
  // 할당과 대수가 스트림이 붙은 동안의 느긋한 폴링 한 바퀴 동안 옛 값이다.
  useRefetchOn((c) => c.kind === 'vm' || c.kind === 'lease', nodes.refresh, HELD_DEBOUNCE_MS)
  // 팀은 팀원이 지금 가진 VM 수(`vm_count`)를 싣는다 — 그 수를 바꾸는 쓰기(VM 생성·파기)도 `vm`·`lease` 만 낸다.
  useRefetchOn((c) => c.kind === 'vm' || c.kind === 'lease', teams.refresh, TEAM_VMS_DEBOUNCE_MS)

  // 검토 대기의 항목은 소유자의 이름을 싣는다 — 이름을 바꾸는 쓰기는 `user` 만 내므로 그 알림에도 다시 읽는다.
  const renamed = (c: Change) => c.kind === 'user' && c.what === 'profile'
  useRefetchOn(
    (c) =>
      c.kind === 'review' ||
      (c.kind === 'node' && (c.what === 'liveness' || c.what === 'state' || c.what === 'membership' || c.what === 'progress')) ||
      c.kind === 'lease' ||
      c.kind === 'vm' ||
      renamed(c) ||
      (c.kind === 'cluster' && c.what !== 'health'),
    review.refresh,
    REVIEW_DEBOUNCE_MS,
  )

  const reads = [clusters, nodes, teams, review]
  const loading = reads.some((r) => r.loading)
  const error = reads.find((r) => r.error)?.error ?? null
  const world = useMemo(
    () =>
      build({
        clusters: clusters.data ?? EMPTY.clusters,
        nodes: nodes.data ?? EMPTY.nodes,
        teams: teams.data ?? EMPTY.teams,
        review: review.data ?? [],
      }),
    [clusters.data, nodes.data, teams.data, review.data],
  )
  const refresh = () => reads.forEach((r) => r.refresh())
  return { world, loading, error, refresh }
}
